import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/lib/contexts/auth-context";
import { apiClient } from "@/lib/api/client";
import { DistrictDto } from "@/lib/types/api";
import { handleApiError } from "@/lib/utils/errors";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import IndicatorDataEntry from "@/components/indicator-data/indicator-data-entry";

export default function DistrictIndicatorsPage() {
  const [district, setDistrict] = useState<DistrictDto | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const { districtId } = useParams<{ districtId: string }>();
  const { user } = useAuth();

  useEffect(() => {
    if (!districtId) {
      navigate("/dashboard");
      return;
    }

    const isUser = user?.roles?.includes("User");
    const hasDistrict = user?.districtId;

    // User with district assignment - can only access their assigned district
    if (isUser && hasDistrict && user.districtId !== districtId) {
      navigate("/dashboard");
      return;
    }

    loadDistrict();
  }, [districtId, user, navigate]);

  const loadDistrict = async () => {
    if (!districtId) return;

    try {
      setIsLoading(true);
      const districtRes = await apiClient.getDistrictById(districtId);
      if (districtRes.success && districtRes.data) {
        setDistrict(districtRes.data);
      }
    } catch (err: unknown) {
      // Silently fail - will show in loading state
      console.error(handleApiError(err, "Failed to load district"));
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return <div className="flex justify-center py-8">Loading...</div>;
  }

  if (!district || !districtId) {
    return (
      <div className="space-y-6">
        <Button variant="outline" size="sm" onClick={() => navigate("/dashboard/indicator-data")}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        <div className="text-center py-8 text-gray-600 dark:text-gray-400">District not found</div>
      </div>
    );
  }

  return (
    <IndicatorDataEntry
      locationId={districtId}
      locationType="district"
      locationName={district.districtName}
    />
  );
}
